import { Fragment, useCallback, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import logo from '../../assets/images/logo.svg';
import { CategoryTab } from '../../components/CategoryTab';
import { ListTab } from '../../components/ListTab';
import { useBuy, useShopPreview } from '../../hooks';
import {
	resetStore,
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledCloseButtonContainer,
	StyledControlsContainer,
	StyledRightContainer,
} from './TattooShop.styled';

const TattooShop = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector(
		(state) => state.tattooShop.activeStage,
	);
	const { handleBuy } = useBuy();
	const shopPreview = useShopPreview();
	const modalState = useObservable(rage.modal.getCurrentModal());

	const handleBackToCategory = useCallback(() => {
		dispatch(setActiveStage(StageTypes.Category));
		rage.tattooShop
			.previewTattooCategory(null)
			.catch((err) => rage.notifications.showException(err));
	}, [dispatch]);

	const leftControls = useMemo(() => {
		if (activeStage === StageTypes.Category) {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Выбрать категорию',
					keys: [InputKey.Enter],
					onClick: () => dispatch(setActiveStage(StageTypes.List)),
				},
				{
					text: 'Крутить камеру',
					keys: [InputKey.MouseRight],
				},
			];
		}

		if (activeStage === StageTypes.List) {
			return [
				{
					text: 'Управление',
					keys: [InputKey.ArrowUp, InputKey.ArrowDown],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				{
					text: 'Крутить камеру',
					keys: [InputKey.MouseRight],
				},
			];
		}

		return [];
	}, [activeStage, handleBuy]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => {
				if (activeStage === StageTypes.List) {
					handleBackToCategory();
				} else {
					rage.tattooShop
						.exitMenu()
						.catch((err) => rage.notifications.showException(err));
				}
			},
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (!modalState) {
			if (activeStage === StageTypes.List) {
				handleBackToCategory();
			} else {
				rage.tattooShop
					.exitMenu()
					.catch((err) => rage.notifications.showException(err));
			}
		}
	});

	useEffectOnce(() => {
		return () => {
			dispatch(resetStore());
		};
	});
	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance />
				<StyledCloseButtonContainer
					onClick={() => {
						rage.tattooShop.exitMenu();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} width={20} height={20} />
				</StyledCloseButtonContainer>
			</StyledRightContainer>
			<ShopContainer>
				<ShopHead img={logo} text={''} height={30} />
				{activeStage === StageTypes.Category && <CategoryTab />}
				{activeStage === StageTypes.List && <ListTab />}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview {...shopPreview} />
		</Fragment>
	);
};

export { TattooShop };
